<template>
  <Modal :is-open="isOpen" @close="close">
    <template #title>{{ title }}</template>
    <template #body>
      <GiftCardResendForm
        v-if="view === 'form' && activeCard"
        :code="activeCard.attributes.code"
        :initial-email="activeCard.attributes.recipient_email || ''"
        :initial-message="activeCard.attributes.gift_card_message || ''"
        @sent="onSent"
        @cancel="onCancel"
      />
      <div v-else>
        <div v-if="isLoading" class="py-8 text-center text-sm text-gray-500">Loading gift cards…</div>
        <div v-else-if="!issuedGiftCards.length" class="py-8 text-center text-sm text-gray-500">
          No gift cards were issued for this order.
        </div>
        <ul v-else class="space-y-3">
          <li v-for="card in issuedGiftCards" :key="card.attributes.code" class="bg-white rounded-lg border px-4 py-3">
            <div class="flex justify-between items-center mb-1">
              <span class="text-md font-semibold text-brand-gray-800">{{ card.attributes.code }}</span>
              <span
                v-if="card.attributes.redeemed"
                class="inline-flex items-center px-2.5 py-1.5 rounded-lg border border-red-300 bg-red-50 text-xs font-semibold text-red-600"
              >
                Gift card already redeemed
              </span>
              <span v-else-if="isSent(card.attributes.code)" class="text-green-600">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </span>
              <SecondaryButton v-else :size="'sm'" @click="openResend(card.attributes.code)">
                Resend Gift Card
              </SecondaryButton>
            </div>
            <div class="flex justify-between items-center mt-2 text-sm font-mono text-gray-brand-900">
              <span>{{ card.attributes.gift_card_value?.format }}</span>
              <span>{{ formatDate(card.attributes.created_at) }}</span>
            </div>
          </li>
        </ul>
      </div>
    </template>
    <template #footer>
      <div v-if="view === 'list'" class="flex justify-end">
        <SecondaryButton :size="'md'" @click="close">Close</SecondaryButton>
      </div>
    </template>
  </Modal>
</template>

<script>
import Modal from '@/components/Molecules/Modal'
import SecondaryButton from '@/components/Molecules/SecondaryButton'
import GiftCardResendForm from '@/components/Checkout/GiftCardResendForm'

export default {
  name: 'ReceiptGiftCardsModal',
  components: { Modal, SecondaryButton, GiftCardResendForm },
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    issuedGiftCards: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  data() {
    return {
      view: 'list',
      activeCode: null,
      sentCodes: [],
      initialViewApplied: false
    }
  },
  computed: {
    activeCard() {
      return this.issuedGiftCards.find((card) => card.attributes?.code === this.activeCode)
    },
    isSingleCard() {
      return this.issuedGiftCards.length === 1
    },
    title() {
      return this.view === 'form' ? 'Resend Gift Card' : 'Gift Cards'
    }
  },
  watch: {
    isOpen(open) {
      if (open) {
        this.initialViewApplied = false
        this.applyInitialView()
      }
    },
    issuedGiftCards() {
      if (this.isOpen) this.applyInitialView()
    }
  },
  methods: {
    applyInitialView() {
      if (this.initialViewApplied || !this.issuedGiftCards.length) return
      this.initialViewApplied = true
      if (this.isSingleCard && !this.issuedGiftCards[0].attributes.redeemed) {
        this.openResend(this.issuedGiftCards[0].attributes.code)
      } else {
        this.view = 'list'
      }
    },
    onCancel() {
      if (this.isSingleCard) this.close()
      else this.backToList()
    },
    formatDate(createdAt) {
      if (!createdAt) return ''
      return new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      }).format(new Date(createdAt))
    },
    isSent(code) {
      return this.sentCodes.includes(code)
    },
    openResend(code) {
      const card = this.issuedGiftCards.find((c) => c.attributes?.code === code)
      if (card?.attributes?.redeemed) return
      this.activeCode = code
      this.view = 'form'
    },
    backToList() {
      this.view = 'list'
      this.activeCode = null
    },
    onSent({ code }) {
      if (!this.sentCodes.includes(code)) this.sentCodes.push(code)
      this.backToList()
    },
    close() {
      this.view = 'list'
      this.activeCode = null
      this.$emit('close')
    }
  }
}
</script>
